// Integrations — fleet-wide third-party credentials (production manager).
//
// One row per known integration. A credential is WRITE-ONLY from here: the
// screen can show that a key is set and when it last changed, replace it, or
// remove it — but the portal is never handed the value back. Only the gateway,
// holding a local-access key, reads values.
//
// Rotation is just another save: the gateway re-reads within its cache TTL, so
// no box edit and no restart.

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useFleetSettings, useSetFleetSetting, useDeleteFleetSetting } from './hooks';
import { QueryState } from '../../components/QueryState';
import type { FleetSetting, FleetSettingsResponse } from '../../lib/types';

const ID = 'em-mgr--integrations';

const FIELD_CLS =
  'w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 ' +
  'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100';
const BTN_PRIMARY =
  'rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 ' +
  'disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300';

/** The integrations the portal knows how to configure, in display order. */
const KNOWN_INTEGRATIONS = [
  {
    name: 'unsplash_access_key',
    label: 'Unsplash',
    blurb:
      'The ONE Unsplash application shared by every Convenely site. The editor agent’s ' +
      'stock-image tools authenticate with it.',
    placeholder: 'Unsplash Access Key',
  },
] as const;

function formatTs(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

interface IntegrationCardProps {
  meta: (typeof KNOWN_INTEGRATIONS)[number];
  row: FleetSetting | undefined;
  /** True when THIS card's remove confirmation is the one open (from the URL). */
  confirming?: boolean;
  /** Open/close the confirmation. Omitted → local state (unit tests). */
  onConfirmingChange?: (name: string | null) => void;
}

function IntegrationCard({
  meta,
  row,
  confirming: routeConfirming,
  onConfirmingChange,
}: IntegrationCardProps) {
  const setSetting = useSetFleetSetting();
  const deleteSetting = useDeleteFleetSetting();

  // The credential VALUE stays local — a write-only secret never enters the URL.
  // Only which card is confirming a removal is addressable.
  const [value, setValue] = useState('');
  const [localConfirming, setLocalConfirming] = useState(false);
  const confirmingRemove = onConfirmingChange ? routeConfirming === true : localConfirming;
  const setConfirmingRemove = (next: boolean) =>
    onConfirmingChange ? onConfirmingChange(next ? meta.name : null) : setLocalConfirming(next);

  const configured = Boolean(row?.set);
  const busy = setSetting.isPending || deleteSetting.isPending;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    setSetting.mutate(
      { name: meta.name, value: value.trim() },
      { onSuccess: () => setValue('') },
    );
  }

  return (
    <section
      data-id={`${ID}--card`}
      className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-800"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="mr-auto font-semibold">{meta.label}</h3>
        {configured ? (
          <span
            data-id={`${ID}--card--status-set`}
            className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300"
          >
            configured
          </span>
        ) : (
          <span
            data-id={`${ID}--card--status-unset`}
            className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          >
            not set
          </span>
        )}
      </div>

      <p className="max-w-2xl text-sm text-slate-500 dark:text-slate-400">{meta.blurb}</p>

      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <div className="flex gap-1">
          <dt>Setting:</dt>
          <dd className="font-mono">{meta.name}</dd>
        </div>
        <div className="flex gap-1">
          <dt>Last changed:</dt>
          <dd data-id={`${ID}--card--updated`}>{formatTs(row?.updated_at ?? null)}</dd>
        </div>
      </dl>

      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <label className="flex-1 text-sm">
          <span className="text-slate-500">
            {configured ? 'Replace key (rotation)' : 'Key'}
          </span>
          <input
            data-id={`${ID}--card--value`}
            className={FIELD_CLS}
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={meta.placeholder}
            required
          />
        </label>
        <button type="submit" data-id={`${ID}--card--save`} disabled={busy} className={BTN_PRIMARY}>
          {setSetting.isPending ? 'Saving…' : configured ? 'Replace' : 'Save'}
        </button>
        {configured &&
          (confirmingRemove ? (
            <span className="flex items-center gap-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">Remove?</span>
              <button
                type="button"
                data-id={`${ID}--card--remove-confirm`}
                disabled={busy}
                onClick={() => {
                  setConfirmingRemove(false);
                  deleteSetting.mutate(meta.name);
                }}
                className="rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Yes, remove
              </button>
              <button
                type="button"
                data-id={`${ID}--card--remove-cancel`}
                onClick={() => setConfirmingRemove(false)}
                className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 dark:border-slate-600 dark:text-slate-200"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              data-id={`${ID}--card--remove`}
              disabled={busy}
              onClick={() => setConfirmingRemove(true)}
              className="rounded border border-red-300 px-2 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/40"
            >
              Remove
            </button>
          ))}
      </form>

      {/* Removing a credential the gateway depends on makes the dependent tool
          fail loud rather than silently authenticate as nobody. Say so. */}
      {configured && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Saving a new key replaces the old one everywhere. Removing it disables the feature
          fleet-wide.
        </p>
      )}

      {setSetting.isError && (
        <p data-id={`${ID}--card--save-error`} className="text-sm text-red-600 dark:text-red-400">
          {setSetting.error instanceof Error ? setSetting.error.message : 'Save failed'}
        </p>
      )}
      {deleteSetting.isError && (
        <p data-id={`${ID}--card--remove-error`} className="text-sm text-red-600 dark:text-red-400">
          {deleteSetting.error instanceof Error ? deleteSetting.error.message : 'Remove failed'}
        </p>
      )}
    </section>
  );
}

export interface IntegrationsScreenProps {
  /** Integration name whose remove confirmation is open (from ?confirmRemove=). */
  confirmRemove?: string | null;
  /** Open/close a confirmation. Omitted → cards keep local state (unit tests). */
  onConfirmRemoveChange?: (name: string | null) => void;
}

export function IntegrationsScreen({
  confirmRemove = null,
  onConfirmRemoveChange,
}: IntegrationsScreenProps = {}) {
  const settings = useFleetSettings();

  return (
    <section data-id={ID} className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Integrations</h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
          Third-party credentials shared by every site in the fleet. Keys are write-only — this
          screen can set, replace, or remove one, but never display it.
        </p>
      </div>

      <QueryState<FleetSettingsResponse>
        idBase={`${ID}--list`}
        isPending={settings.isPending}
        isError={settings.isError}
        error={settings.error}
        data={settings.data}
      >
        {(data) => {
          const byName = new Map(data.settings.map((s) => [s.name, s]));
          return (
            <div className="space-y-4">
              {KNOWN_INTEGRATIONS.map((meta) => (
                <IntegrationCard
                  key={meta.name}
                  meta={meta}
                  row={byName.get(meta.name)}
                  confirming={confirmRemove === meta.name}
                  onConfirmingChange={onConfirmRemoveChange}
                />
              ))}
            </div>
          );
        }}
      </QueryState>
    </section>
  );
}
